/**
 * `/view/<public_id>` — a mockup, reviewed (session 49, `.docs/49-mockup-pins-and-guests.md`;
 * the one place a mockup is reviewed since session 55, `.docs/55-mockup-comments.md`).
 *
 * The studio's page **full-screen**, and the portal docked in one corner — a bar that opens into
 * the list of threads, which on a wide screen is a column down the right edge with the page
 * beside it. Comments are made **on the page itself**: select text, right-click, or press `C`
 * and click a spot or drag an area. A thread opens in a card beside its pin.
 *
 * Who is looking decides the corner and what they may do, and the server says both
 * (`MockupLinkViewer`):
 *
 *   - **a guest** — no account here, or another client's — comments and replies under a name
 *     they give, remembered for next time, and is offered *Sign in*, which comes back here;
 *   - **a member** of the client comments, replies, resolves and reopens as themselves, through
 *     their own routes, and is offered their portal page, where Approve and Request changes are;
 *   - **the studio** does all of that through the tools, and hides a guest's words.
 *
 * Nobody answers a review here: that is a member's sign-off and lives on their portal page.
 */

import type { MockupCommentView, MockupLinkView } from '@fareast/shared'
import { MOCKUP_VIEW_PREFIX } from '@fareast/shared'
import { useEffect, useReducer, useRef, useState } from 'react'
import { z } from 'zod'

import { useMockupLink } from '../../api/client/index.js'
import { Button, Seg, SegItem } from '../../components/portal/Button.js'
import { Dock, PREVIEW_WIDTHS, Preview, type PreviewWidth } from '../../components/portal/Files.js'
import { Row, Stack } from '../../components/portal/Layout.js'
import { Splash, SplashCard, SplashForm } from '../../components/portal/Splash.js'
import { Notice } from '../../components/portal/Status.js'
import { B, Muted, Prose, Small } from '../../components/portal/Text.js'
import { usePinBridge } from '../../pins/usePinBridge.js'
import { useSearchState } from '../../search/useSearchState.js'
import { shortcutsApply } from '../../shell/shortcuts.js'
import { useBodyClass, useDocumentTitle } from '../../shell/useBodyClass.js'
import { useReviewDoors, type NewComment } from './doors.js'
import { isOpenThread, numberedPins, whereOf } from './pins.js'
import { initialReview, reviewReducer } from './review.js'
import { NewThread, PlacedCard, Thread } from './ThreadCard.js'
import { ThreadList, placeWords, type ThreadTab } from './ThreadList.js'
import { COMMENTS_ARE_SHARED, GESTURE_HINT, WIDTH_WORDS, versionWord } from './words.js'

/** Where a guest's name is kept between visits — on this device, and nowhere else. */
export const GUEST_NAME_KEY = 'fes.mockups.guest-name'

/**
 * `?v=<n>` — the version shown; `?c=<id>` — the thread open. How the portal pages link into a
 * review, and what a person copies to point somebody else at one comment.
 */
export const ReviewSearchSchema = z.object({
  v: z.coerce.number().int().min(1).optional().catch(undefined),
  c: z.uuid().optional().catch(undefined),
})

export function LinkViewerPage({ publicId }: { readonly publicId: string }) {
  const query = useMockupLink(publicId)

  if (query.data === undefined) {
    return <NoMockup waiting={query.isPending} />
  }
  return <Review publicId={publicId} view={query.data} />
}

/** Opening, or a link that leads nowhere — the preview's own sentence for the second. */
function NoMockup({ waiting }: { readonly waiting: boolean }) {
  useBodyClass('splash')
  useDocumentTitle('A mockup — Far East Studios')
  return (
    <Splash>
      <SplashForm>
        <SplashCard title={waiting ? 'Opening the mockup' : 'There is no mockup here'}>
          <Prose>
            <Muted>
              {waiting
                ? 'One moment…'
                : 'There is no mockup at this address. Ask the studio for a fresh link.'}
            </Muted>
          </Prose>
        </SplashCard>
      </SplashForm>
    </Splash>
  )
}

function Review({ publicId, view }: { readonly publicId: string; readonly view: MockupLinkView }) {
  useDocumentTitle(`${view.title} — Far East Studios`)
  const [search, setSearch] = useSearchState(ReviewSearchSchema)
  const viewer = view.viewer
  const [state, dispatch] = useReducer(reviewReducer, search.c ?? null, initialReview)
  // A guest opens on the page and one corner; anybody else came to review, and opens on the list.
  const [panel, setPanel] = useState(viewer.kind !== 'guest' || search.c !== undefined)
  const [tab, setTab] = useState<ThreadTab>('open')
  const [width, setWidth] = useState<PreviewWidth>('desktop')
  const [guestName, setGuestName] = useState(storedGuestName)
  const doors = useReviewDoors(publicId, viewer)
  const frame = useRef<HTMLIFrameElement>(null)
  const area = useRef<HTMLDivElement>(null)
  const wide = useWide()

  const version = view.versions.find((one) => one.number === search.v) ?? view.versions[0]
  const threads = view.comments.filter((one) => one.version === version?.number)
  const { pins, numberOf } = numberedPins(threads)
  const openThread = threads.find((one) => one.id === state.openId) ?? null
  const openN = openThread === null ? undefined : numberOf.get(openThread.id)
  // Resolved pins are on the page while the resolved list is shown, and the open one always.
  const drawn = pins.filter((one) => !one.resolved || tab === 'resolved' || one.n === openN)

  const bridge = usePinBridge({
    frame,
    versionUrl: version?.url ?? '',
    pins: drawn,
    mode: state.mode,
    gestures: viewer.may_comment,
    draft: state.draft?.pin ?? null,
    active: openN ?? null,
    onPlace: (pin) => {
      dispatch({ type: 'place', pin })
    },
    onSelect: (n) => {
      const thread = threads.find((one) => numberOf.get(one.id) === n)
      if (thread !== undefined) open(thread)
    },
    onKey: (key) => {
      onKey(key)
    },
  })

  function open(thread: MockupCommentView) {
    dispatch({ type: 'open', id: thread.id })
    const n = numberOf.get(thread.id)
    if (n !== undefined) bridge.focus(n)
  }

  function onKey(key: 'escape' | 'comment') {
    if (key === 'escape') dispatch({ type: 'escape' })
    else if (viewer.may_comment) {
      dispatch({ type: 'mode', mode: state.mode === 'comment' ? 'browse' : 'comment' })
    }
  }

  // The same two keys when the keyboard is in the portal rather than in the page.
  const keys = useRef(onKey)
  keys.current = onKey
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if ((event.key === 'c' || event.key === 'C') && shortcutsApply(event)) {
        keys.current('comment')
        return
      }
      // Escape closes the card from inside its own box too — but a dialog's is the dialog's.
      if (event.key !== 'Escape' || event.defaultPrevented || event.repeat) return
      const target = event.target instanceof Element ? event.target : null
      if (target !== null && target.closest('[role="dialog"]') !== null) return
      keys.current('escape')
    }
    window.addEventListener('keydown', onKeyDown)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [])

  // The open thread is in the address, so the address points somebody at it.
  useEffect(() => {
    if ((search.c ?? null) !== state.openId) {
      setSearch({ c: state.openId ?? undefined }, { replace: true })
    }
  }, [state.openId, search.c, setSearch])

  // A thread the address named is brought into view once the page in the frame has spoken.
  const arrived = useRef(false)
  useEffect(() => {
    if (arrived.current || bridge.page === null) return
    arrived.current = true
    if (openN !== undefined) bridge.focus(openN)
  }, [bridge, openN])

  if (version === undefined) return <NoMockup waiting={false} />

  async function post(comment: NewComment) {
    await doors.comment(comment)
    if (viewer.kind === 'guest' && comment.name !== undefined) {
      rememberGuestName(comment.name)
      setGuestName(comment.name)
    }
  }

  const asGuest = viewer.kind === 'guest' ? guestName : undefined
  const looking = { page: bridge.page, shown: bridge.shown, width }
  const openWhere = openThread === null ? null : whereOf(openThread, openN, looking)
  const openCount = threads.filter(isOpenThread).length
  const tucked = state.mode === 'comment' && !wide

  return (
    <>
      <Preview
        // Its own key: a new version is a new page, and the bridge on it says so.
        key={`frame-v${String(version.number)}`}
        src={version.url}
        title={`${view.title}, ${versionWord(version).toLowerCase()}`}
        width={width}
        fill
        beside={panel && !tucked}
        frameRef={frame}
        areaRef={area}
      />

      {state.draft === null ? null : (
        <PlacedCard
          key={`draft-${String(state.draft.key)}`}
          where={state.draft.pin === null ? null : bridge.where}
          frame={frame}
          area={area}
          label="New comment"
        >
          <NewThread
            pin={state.draft.pin}
            guestName={asGuest}
            onPost={(comment) =>
              post({
                ...comment,
                version: version.number,
                pin: state.draft?.pin ?? undefined,
              })
            }
            onClose={() => {
              dispatch({ type: 'close' })
            }}
          />
        </PlacedCard>
      )}
      {openThread === null ? null : (
        <PlacedCard
          key={openThread.id}
          where={openWhere?.kind === 'here' ? bridge.where : null}
          frame={frame}
          area={area}
          label={openN === undefined ? 'Comment' : `Comment ${String(openN)}`}
        >
          <Thread
            thread={openThread}
            n={openN}
            viewer={viewer}
            guestName={asGuest}
            notice={
              openWhere === null || openWhere.kind === 'here' ? null : (
                <WhereNotice
                  words={placeWords(openWhere)}
                  width={openWhere.kind === 'hidden' ? openWhere.width : null}
                  onWidth={setWidth}
                />
              )
            }
            onReply={(comment) =>
              post({ ...comment, version: version.number, reply_to: openThread.id })
            }
            onResolve={async () => {
              await doors.resolve(openThread.id)
              dispatch({ type: 'close' })
            }}
            onReopen={() => doors.reopen(openThread.id)}
            onClose={() => {
              dispatch({ type: 'close' })
            }}
          />
        </PlacedCard>
      )}

      <Dock
        // While commenting on a small screen, the dock is its bar and nothing more: open, it
        // covers the page, and the page is what has to be tapped. Tucked, not closed, so where
        // the list was is still there after.
        open={panel}
        tucked={tucked}
        panel
        label="Comments on this mockup"
        bar={
          state.mode === 'comment' ? (
            <>
              <Small>Click a spot, or drag over an area.</Small>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  dispatch({ type: 'mode', mode: 'browse' })
                }}
              >
                Stop commenting
              </Button>
            </>
          ) : (
            <>
              <Button
                size="sm"
                variant={panel ? 'default' : 'primary'}
                aria-expanded={panel}
                onClick={() => {
                  setPanel(!panel)
                }}
              >
                {panel ? 'Close' : openCount === 0 ? 'Comments' : `Comments · ${String(openCount)}`}
              </Button>
              {viewer.may_comment ? (
                <Button
                  size="sm"
                  variant="ghost"
                  aria-keyshortcuts="C"
                  onClick={() => {
                    dispatch({ type: 'mode', mode: 'comment' })
                  }}
                >
                  Comment
                </Button>
              ) : null}
              <ViewerLink viewer={viewer} publicId={publicId} />
            </>
          )
        }
      >
        <Stack>
          <Stack gap="tight">
            <B>{view.title}</B>
            {view.versions.length < 2 ? (
              <Small>
                <Muted>{versionWord(version)}</Muted>
              </Small>
            ) : (
              <Seg label="Version">
                {[...view.versions].reverse().map((one) => (
                  <SegItem
                    key={one.number}
                    on={one.number === version.number}
                    onClick={() => {
                      dispatch({ type: 'close' })
                      setSearch({ v: one.number, c: undefined }, { replace: true })
                    }}
                  >
                    {`v${String(one.number)}`}
                  </SegItem>
                ))}
              </Seg>
            )}
            <Seg label="Width">
              {PREVIEW_WIDTHS.map((one) => (
                <SegItem
                  key={one}
                  on={one === width}
                  onClick={() => {
                    setWidth(one)
                  }}
                >
                  {WIDTH_WORDS[one]}
                </SegItem>
              ))}
            </Seg>
            {version.note === '' ? null : (
              <Small>
                <Muted>{version.note}</Muted>
              </Small>
            )}
          </Stack>
          {viewer.may_comment ? (
            <Stack gap="tight">
              <Small>
                <Muted>{GESTURE_HINT}</Muted>
              </Small>
              <Row>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    dispatch({ type: 'compose' })
                  }}
                >
                  Comment on the whole version
                </Button>
              </Row>
            </Stack>
          ) : (
            <Small>
              <Muted>
                {viewer.kind === 'studio'
                  ? 'The studio’s managers answer and resolve comments; your seat reads them.'
                  : 'Your seat on this account does not comment on designs.'}
              </Muted>
            </Small>
          )}
          <ThreadList
            threads={threads}
            numberOf={numberOf}
            openId={state.openId}
            tab={tab}
            onTab={setTab}
            looking={looking}
            onOpen={open}
          />
          <Small>
            <Muted>{COMMENTS_ARE_SHARED}</Muted>
          </Small>
          {viewer.kind === 'guest' && !viewer.signed_in ? (
            <Small>
              <Muted>
                Is this design yours? <SignIn publicId={publicId} /> to approve it or ask for
                changes.
              </Muted>
            </Small>
          ) : null}
          {viewer.kind === 'guest' && viewer.signed_in ? (
            <Small>
              <Muted>
                You are signed in to another account. If this design is for one of yours,{' '}
                <a href="/app">switch to it in your portal</a> to approve it or ask for changes.
              </Muted>
            </Small>
          ) : null}
        </Stack>
      </Dock>
    </>
  )
}

/** Where an open thread's pin is, when it is not on the page shown — and the width it was left at. */
function WhereNotice({
  words,
  width,
  onWidth,
}: {
  readonly words: string | null
  readonly width: PreviewWidth | null
  readonly onWidth: (width: PreviewWidth) => void
}) {
  if (words === null) return null
  return (
    <Notice role="status">
      {words}.{' '}
      {width === null ? null : (
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            onWidth(width)
          }}
        >
          {`View at ${WIDTH_WORDS[width].toLowerCase()} width`}
        </Button>
      )}
    </Notice>
  )
}

/** The bar's last button: sign in, or your own copy of this mockup. */
function ViewerLink({
  viewer,
  publicId,
}: {
  readonly viewer: MockupLinkView['viewer']
  readonly publicId: string
}) {
  switch (viewer.kind) {
    case 'guest':
      // Signed in to another account already: signing in again would change nothing.
      return viewer.signed_in ? null : (
        <Button size="sm" variant="ghost" href={signInAddress(publicId)}>
          Sign in
        </Button>
      )
    case 'member':
      return (
        <Button size="sm" variant="ghost" href={viewer.portal_url}>
          Open in your portal
        </Button>
      )
    case 'studio':
      return (
        <Button size="sm" variant="ghost" href={viewer.portal_url}>
          Open in the studio
        </Button>
      )
  }
}

function SignIn({ publicId }: { readonly publicId: string }) {
  return <a href={signInAddress(publicId)}>Sign in</a>
}

/** Sign in, and come back to this link — `/login` takes any path on this host. */
export function signInAddress(publicId: string): string {
  return `/login?redirect=${encodeURIComponent(`${MOCKUP_VIEW_PREFIX}/${publicId}`)}`
}

/** A screen wide enough for the list and the page side by side — `.dock--panel`'s breakpoint. */
function useWide(): boolean {
  const query = '(min-width: 1024px)'
  const [wide, setWide] = useState(() => mediaMatches(query))
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return
    const media = window.matchMedia(query)
    const changed = () => {
      setWide(media.matches)
    }
    media.addEventListener('change', changed)
    return () => {
      media.removeEventListener('change', changed)
    }
  }, [])
  return wide
}

function mediaMatches(query: string): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia(query).matches
}

function storedGuestName(): string {
  try {
    return window.localStorage.getItem(GUEST_NAME_KEY) ?? ''
  } catch {
    // Storage switched off, or full: the box starts empty, which is all this was saving.
    return ''
  }
}

function rememberGuestName(name: string): void {
  try {
    window.localStorage.setItem(GUEST_NAME_KEY, name)
  } catch {
    // As above — remembering is a convenience, never a reason to refuse a comment.
  }
}
